import { useEffect, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { toLatinDigits, toPersianDigits } from '../lib/fa'
import { Button } from './ui'

/** Long enough to skip the keystrokes of a mobile number, short enough not to feel stuck. */
const DEBOUNCE_MS = 300

export const fieldClass =
  'h-10 rounded-xl border border-line bg-card px-3 text-strong outline-none transition-colors placeholder:text-muted/60 focus:border-brand-500'

/**
 * Filters and page of a list, kept in the URL so a result can be shared, reloaded and returned
 * to. Changing a filter starts over at page one and replaces history; turning a page adds to it.
 */
export function useListParams() {
  const [params, setParams] = useSearchParams()
  const page = Math.max(1, Number(params.get('page')) || 1)

  const setFilter = (key: string, value: string | undefined) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value) next.set(key, value)
        else next.delete(key)
        next.delete('page')
        return next
      },
      { replace: true },
    )

  const goTo = (target: number) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      if (target > 1) next.set('page', String(target))
      else next.delete('page')
      return next
    })

  return {
    params,
    page,
    get: (key: string) => params.get(key) ?? '',
    setFilter,
    goTo,
    clear: () => setParams({}),
  }
}

/**
 * A text box that writes to the URL once typing pauses. `digits` keeps only digits (Persian
 * ones are accepted and stored Latin), for amounts.
 */
export function DebouncedInput({
  value,
  onCommit,
  digits = false,
  className = '',
  ...props
}: {
  value: string
  onCommit: (value: string) => void
  digits?: boolean
  className?: string
  placeholder?: string
  type?: string
  autoFocus?: boolean
}) {
  const clean = (raw: string) => (digits ? toLatinDigits(raw).replace(/\D/g, '') : raw.trim())
  const [draft, setDraft] = useState(digits ? toPersianDigits(value) : value)
  // Back/forward changes the URL under the box; our own update must not eat a trailing space.
  useEffect(
    () => setDraft((d) => (clean(d) === value ? d : digits ? toPersianDigits(value) : value)),
    [value],
  )

  useEffect(() => {
    const next = clean(draft)
    if (next === value) return
    const timer = setTimeout(() => onCommit(next), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [draft, value])

  return (
    <input
      {...props}
      inputMode={digits ? 'numeric' : undefined}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      className={`${fieldClass} ${className}`}
    />
  )
}

export function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-strong">{label}</span>
      {children}
    </label>
  )
}

export function Pager({
  page,
  total,
  pageSize,
  noun,
  busy,
  onGo,
}: {
  page: number
  total: number
  pageSize: number
  noun: string
  busy: boolean
  onGo: (page: number) => void
}) {
  if (total === 0) return null
  const pages = Math.max(1, Math.ceil(total / pageSize))
  return (
    <div className="mt-4 flex items-center gap-3 text-sm">
      <Button variant="secondary" disabled={page <= 1 || busy} onClick={() => onGo(page - 1)}>
        قبلی
      </Button>
      <span className="text-muted">
        صفحه {toPersianDigits(page)} از {toPersianDigits(pages)} — {toPersianDigits(total)} {noun}
      </span>
      <Button variant="secondary" disabled={page >= pages || busy} onClick={() => onGo(page + 1)}>
        بعدی
      </Button>
    </div>
  )
}
